/*
 * NotificationCenter — the redesigned two-rail Notification Center (from the
 * Claude Design "Black Sheep Global Design System" handoff). Exports are kept
 * identical to the previous version (NotificationBell{onOpen} +
 * NotificationPopover{anchorEl,onClose,activityLog,onClearActivity}) so the
 * frozen App.tsx mount is untouched. High-fidelity to the spec: design tokens
 * only (var(--…)), honors prefers-reduced-motion.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, IconButton, Popover, Tooltip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNotifications } from '../../contexts/NotificationContext';
import type { NotificationItem } from '../../services/notificationsCenterApi';
import { formatTimeAgo } from '../../utils/timeFormat';
import { HubSettingsGear } from '../hub/HubSettingsGear';
import { GEAR } from '../settings/gearSurfaces';

const HEAD = 'var(--font-head)';

// ── Icons (simple 24×24 line glyphs, matching the spec inventory) ──────────
const PATHS: Record<string, string> = {
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  general: 'M4 4h16v12H7l-3 3z',
  scorecard: 'M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z',
  tickets: 'M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4zM10 5v14',
  scheduling: 'M8 2v4M16 2v4M3 9h18M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
  forms: 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4',
  deals: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  projects: 'M2 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z',
  clients: 'M16 19v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6M22 19v-2a4 4 0 0 0-3-3.9M16 3.1A4 4 0 0 1 16 11',
  campaign: 'M3 11l18-5v12L3 14zM11.6 16.8a3 3 0 1 1-5.8-1.6',
  engineering: 'M8 6l-5 6 5 6M16 6l5 6-5 6M13 4l-2 16',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1A1.6 1.6 0 0 0 6.6 19.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 14.6a2 2 0 0 1 0-4 1.6 1.6 0 0 0 1.5-1.1l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H10a1.6 1.6 0 0 0 1-1.5V4a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V10a1.6 1.6 0 0 0 1.5 1h.1a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.4 1z',
  snooze: 'M12 8v4l2.5 1.5M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M3 4l3 2M21 4l-3 2',
  check: 'M5 12l4.5 4.5L19 7',
  checkDouble: 'M2 13l4 4 8-8M12 13l2 2 8-8',
  open: 'M14 3h7v7M21 3l-9 9M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5',
  x: 'M6 6l12 12M18 6L6 18',
  todos: 'M4 5h3v3H4zM10 6.5h10M4 11h3v3H4zM10 12.5h10M4 17h3v3H4zM10 18.5h10',
};

function Icon({ d, size = 18, color = 'currentColor', sw = 1.7 }: { d: string; size?: number; color?: string; sw?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw}
      strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
      <path d={d} />
    </svg>
  );
}

// Left-rail categories (mirrors the backend taxonomy; counts come from summary).
interface Cat { id: string; label: string; blurb: string; tone: 'indigo' | 'paprika'; icon: string }
const CATEGORIES: Cat[] = [
  { id: 'general', label: 'General', blurb: 'System & account activity', tone: 'indigo', icon: 'general' },
  { id: 'scorecard', label: 'Scorecard', blurb: 'KPI thresholds & weekly reviews', tone: 'indigo', icon: 'scorecard' },
  { id: 'tickets', label: 'Tickets', blurb: 'Support & internal requests', tone: 'paprika', icon: 'tickets' },
  { id: 'scheduling', label: 'Scheduling', blurb: 'Calls, meetings & bookings', tone: 'indigo', icon: 'scheduling' },
  { id: 'forms', label: 'Form Submissions', blurb: 'Intake & web form activity', tone: 'indigo', icon: 'forms' },
  { id: 'deals', label: 'Deals', blurb: 'Pipeline stage changes', tone: 'indigo', icon: 'deals' },
  { id: 'projects', label: 'Projects', blurb: 'Sprints, tasks & delivery', tone: 'indigo', icon: 'projects' },
  { id: 'clients', label: 'Clients', blurb: 'Account & relationship updates', tone: 'indigo', icon: 'clients' },
  { id: 'campaigns', label: 'Campaigns', blurb: 'Marketing campaign health & sends', tone: 'paprika', icon: 'campaign' },
  { id: 'comms', label: 'Comms', blurb: 'Texts, calls & voicemail on my lines', tone: 'indigo', icon: 'scheduling' },
  { id: 'engineering', label: 'Engineering', blurb: 'QA sweeps, findings & agent health', tone: 'indigo', icon: 'engineering' },
  { id: 'todos', label: 'To-dos', blurb: 'Reminders for what is due', tone: 'indigo', icon: 'todos' },
];

// ── Time helpers ───────────────────────────────────────────────────────────
function parseServerDate(s: string): Date {
  // SQLite datetime('now') is UTC 'YYYY-MM-DD HH:MM:SS' — pin the zone.
  return /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? new Date(s) : new Date(s.replace(' ', 'T') + 'Z');
}
type Bucket = 'today' | 'yesterday' | 'earlier';
function bucketOf(createdAt: string): Bucket {
  const d = parseServerDate(createdAt);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday); startYesterday.setDate(startYesterday.getDate() - 1);
  if (d >= startToday) return 'today';
  if (d >= startYesterday) return 'yesterday';
  return 'earlier';
}
function timeLabel(createdAt: string): string {
  const d = parseServerDate(createdAt);
  const b = bucketOf(createdAt);
  if (b === 'today' || b === 'yesterday') return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const days = (Date.now() - d.getTime()) / 86400000;
  return days < 7 ? d.toLocaleDateString([], { weekday: 'short' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
function snoozeLabel(s: string): string {
  const d = parseServerDate(s);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const b = bucketOf(s);
  if (b === 'today') return `Today · ${time}`;
  const days = (d.getTime() - Date.now()) / 86400000;
  if (days < 2) return `Tomorrow · ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
}
const BUCKETS: Array<[Bucket, string]> = [['today', 'Today'], ['yesterday', 'Yesterday'], ['earlier', 'Earlier']];

// ─────────────────────────────────────────────────────────────────────────
export function NotificationBell({ onOpen }: { onOpen: (e: React.MouseEvent<HTMLElement>) => void }) {
  const { unreadCount } = useNotifications();
  return (
    <Tooltip title="Notifications">
      <IconButton
        aria-label="Notifications"
        color="inherit"
        onClick={onOpen}
        sx={{ ml: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Badge
          badgeContent={unreadCount}
          max={99}
          sx={{ '& .MuiBadge-badge': { bgcolor: 'var(--paprika)', color: 'var(--on-paprika)', fontFamily: HEAD, fontWeight: 700, fontSize: 10.5 } }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────
function Segmented({ value, onChange }: { value: 'all' | 'unread'; onChange: (v: 'all' | 'unread') => void }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--rule)', borderRadius: 4, overflow: 'hidden', marginRight: 4 }}>
      {(['all', 'unread'] as const).map((v) => {
        const on = value === v;
        return (
          <button key={v} onClick={() => onChange(v)} style={{
            padding: '5px 11px', border: 'none', cursor: 'pointer', background: on ? 'var(--ink)' : 'transparent',
            color: on ? '#fff' : 'var(--ink-soft)', fontFamily: HEAD, fontWeight: 600, fontSize: 11, transition: 'all 100ms',
          }}>{v === 'all' ? 'All' : 'Unread'}</button>
        );
      })}
    </div>
  );
}

function IconBtn({ icon, title, onClick }: { icon: string; title: string; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button title={title} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', border: '1px solid', borderColor: h ? 'var(--rule-strong)' : 'transparent', background: h ? 'var(--paper)' : 'transparent', borderRadius: 4, cursor: 'pointer', transition: 'all 100ms' }}>
      <Icon d={PATHS[icon]} size={16} color="var(--ink-soft)" />
    </button>
  );
}

function CatItem({ cat, active, count, onClick }: { cat: { id: string; label: string; icon: string; tone?: string }; active: boolean; count: number; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        position: 'relative', display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
        padding: '10px 14px 10px 16px', border: 'none', cursor: 'pointer',
        background: active ? 'var(--paper)' : (h ? 'rgba(127,127,127,0.06)' : 'transparent'),
        boxShadow: active ? 'inset 2px 0 0 var(--indigo)' : 'none', transition: 'background 100ms',
      }}>
      <Icon d={PATHS[cat.icon]} size={17} color={active ? 'var(--indigo)' : 'var(--ink-faint)'} />
      <span style={{ flex: 1, minWidth: 0, fontFamily: HEAD, fontWeight: active ? 600 : 500, fontSize: 13.5, color: active ? 'var(--ink)' : 'var(--ink-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.label}</span>
      {count > 0 && (
        <span style={{
          flex: 'none', minWidth: 19, height: 18, padding: '0 5px', display: 'grid', placeItems: 'center',
          fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, borderRadius: '6px', color: '#fff',
          background: cat.tone === 'paprika' ? 'var(--paprika)' : (active ? 'var(--indigo)' : 'var(--ink)'),
        }}>{count}</span>
      )}
    </button>
  );
}

function RowAction({ icon, label, onClick, primary }: { icon: string; label: string; onClick: () => void; primary?: boolean }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 9px', cursor: 'pointer',
        border: '1px solid', borderColor: h ? (primary ? 'var(--indigo)' : 'var(--rule-strong)') : 'transparent',
        background: h ? (primary ? 'var(--indigo-soft)' : 'var(--paper)') : 'transparent',
        color: primary ? 'var(--indigo-on-soft)' : 'var(--ink-soft)',
        fontFamily: HEAD, fontWeight: 600, fontSize: 11, borderRadius: 4, transition: 'all 100ms',
      }}>
      <Icon d={PATHS[icon]} size={13} color={primary ? 'var(--indigo-on-soft)' : 'var(--ink-faint)'} sw={1.8} />
      {label}
    </button>
  );
}

function NotifRow({ n, onOpen, onSnooze, onToggleRead }: {
  n: NotificationItem;
  onOpen: (n: NotificationItem) => void;
  onSnooze: (n: NotificationItem) => void;
  onToggleRead: (n: NotificationItem) => void;
}) {
  const [hover, setHover] = useState(false);
  const isRead = !!n.is_read;
  const dotColor = n.priority === 'high' ? 'var(--paprika)' : 'var(--indigo)';
  const actor = (n.type?.split('.')[0] || n.category || 'update');
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', display: 'flex', gap: 13, padding: '15px 18px',
        borderBottom: '1px solid var(--rule-soft)',
        background: hover ? 'var(--paper-2)' : (isRead ? 'transparent' : 'rgba(4,67,239,0.035)'),
        transition: 'background 110ms',
      }}>
      <div style={{ width: 8, flex: 'none', display: 'flex', justifyContent: 'center', paddingTop: 6 }}>
        {!isRead && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, display: 'block' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 3 }}>
          <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: n.priority === 'high' ? 'var(--paprika)' : 'var(--ink-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{actor}</span>
          <span style={{ marginLeft: 'auto', flex: 'none', fontSize: 11, color: 'var(--ink-ghost)' }}>{timeLabel(n.created_at)}</span>
        </div>
        <div style={{ fontFamily: HEAD, fontWeight: isRead ? 500 : 600, fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.35 }}>{n.title}</div>
        {n.body && <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', lineHeight: 1.45, marginTop: 3 }}>{n.body}</div>}
        {n.snoozed_until && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11, color: 'var(--ink-faint)', background: 'var(--paper-2)', border: '1px solid var(--rule)', padding: '3px 8px' }}>
            <Icon d={PATHS.snooze} size={13} color="var(--ink-faint)" /> Snoozed · {snoozeLabel(n.snoozed_until)}
          </div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginTop: hover ? 9 : 0,
          maxHeight: hover ? 30 : 0, opacity: hover ? 1 : 0, overflow: 'hidden',
          transition: 'max-height 130ms ease, opacity 110ms ease, margin-top 130ms ease',
        }}>
          {n.link && <RowAction icon="open" label={n.source_label || 'Open'} onClick={() => onOpen(n)} primary />}
          <RowAction icon="snooze" label="Snooze" onClick={() => onSnooze(n)} />
          <RowAction icon={isRead ? 'general' : 'check'} label={isRead ? 'Unread' : 'Mark read'} onClick={() => onToggleRead(n)} />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ filter }: { filter: 'all' | 'unread' }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40, textAlign: 'center' }}>
      <div style={{ width: 46, height: 46, display: 'grid', placeItems: 'center', border: '1px solid var(--rule)', borderRadius: 6, background: 'var(--paper-2)' }}>
        <Icon d={PATHS.checkDouble} size={22} color="var(--ink-faint)" />
      </div>
      <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{filter === 'unread' ? 'You’re all caught up' : 'Nothing here yet'}</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', maxWidth: 220, lineHeight: 1.5 }}>{filter === 'unread' ? 'No unread notifications in this category.' : 'New notifications for this category will appear here.'}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
export function NotificationPopover({
  anchorEl, onClose, activityLog, onClearActivity,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  activityLog: Array<{ id: string; message: string; time: Date; type: string }>;
  onClearActivity: () => void;
}) {
  const { notifications, summary, refresh, markAsRead, markAsUnread, markAllAsRead, snooze } = useNotifications();
  const navigate = useNavigate();
  const [selectedCat, setSelectedCat] = useState<string>('all');
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const open = Boolean(anchorEl);

  useEffect(() => { if (open) void refresh(); }, [open, refresh]);

  const totalUnread = summary.total_unread;

  const visible = useMemo(() => notifications.filter((n) =>
    (selectedCat === 'all' || n.category === selectedCat) && (filter === 'all' || !n.is_read)
  ), [notifications, selectedCat, filter]);

  const grouped = useMemo(() => BUCKETS
    .map(([key, label]) => [label, visible.filter((n) => bucketOf(n.created_at) === key)] as [string, NotificationItem[]])
    .filter(([, arr]) => arr.length), [visible]);

  const activeCat = selectedCat === 'all' ? null : CATEGORIES.find((c) => c.id === selectedCat);

  const handleOpenSource = async (n: NotificationItem) => {
    if (!n.is_read) await markAsRead(n.id);
    if (n.link) { navigate(n.link); onClose(); }
  };
  const handleToggleRead = (n: NotificationItem) => { n.is_read ? void markAsUnread(n.id) : void markAsRead(n.id); };
  const openPrefs = () => { onClose(); navigate('/settings?tab=notifications'); };

  const showActivity = selectedCat === 'all' && filter === 'all' && activityLog.length > 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: {
        width: 720, maxWidth: 'calc(100vw - 40px)', height: 600, maxHeight: 'calc(100vh - 96px)',
        mt: '6px', mr: '4px', borderRadius: '6px', overflow: 'hidden',
        background: 'var(--paper)', border: '1px solid var(--rule-strong)',
        boxShadow: 'var(--modal)',
        '@media (prefers-reduced-motion: no-preference)': {
          animation: 'bsgNotifIn 150ms cubic-bezier(.2,.7,.3,1)',
        },
      } }}
    >
      <style>{`@keyframes bsgNotifIn{from{opacity:.4;transform:translateY(-4px) scale(.99)}to{opacity:1;transform:none}}`}</style>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px 14px', borderBottom: '1px solid var(--rule)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 14, letterSpacing: '-.01em', color: 'var(--ink)' }}>Notifications</span>
            {totalUnread > 0 && <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, color: 'var(--ink-faint)' }}>{totalUnread} unread</span>}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Segmented value={filter} onChange={setFilter} />
            <IconBtn icon="checkDouble" title="Mark all as read" onClick={() => markAllAsRead(selectedCat === 'all' ? undefined : selectedCat)} />
            {/* The one settings gear (Settings and the gear, Part 1 #5) — a plain link to
                Settings → Notifications; the wrapper closes the bell as it goes. */}
            <span onClick={onClose} style={{ display: 'inline-flex' }}>
              <HubSettingsGear settings={GEAR.notifications.targets} surface={GEAR.notifications.label} />
            </span>
            <IconBtn icon="x" title="Close" onClick={onClose} />
          </div>
        </div>

        {/* two rails */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* left rail */}
          <div style={{ width: 'max(186px, 29%)', flex: 'none', borderRight: '1px solid var(--rule)', background: 'var(--paper-2)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div style={{ padding: '12px 14px 6px', fontFamily: HEAD, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>Categories</div>
            <CatItem cat={{ id: 'all', label: 'All notifications', icon: 'bell' }} active={selectedCat === 'all'} count={totalUnread} onClick={() => setSelectedCat('all')} />
            <div style={{ height: 6 }} />
            {CATEGORIES.map((c) => (
              <CatItem key={c.id} cat={c} active={selectedCat === c.id} count={summary.per_category[c.id] || 0} onClick={() => setSelectedCat(c.id)} />
            ))}
          </div>

          {/* right rail */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: '1px solid var(--rule)' }}>
              <Icon d={PATHS[activeCat?.icon || 'bell']} size={15} color="var(--ink-soft)" />
              <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 13.5, color: 'var(--ink)' }}>{activeCat?.label || 'All notifications'}</span>
              {activeCat && <span style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>· {activeCat.blurb}</span>}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-ghost)' }}>{visible.length} {filter === 'unread' ? 'unread' : 'shown'}</span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {grouped.length === 0 && !showActivity && <EmptyState filter={filter} />}
              {grouped.map(([label, arr]) => (
                <div key={label}>
                  <div style={{ position: 'sticky', top: 0, zIndex: 1, padding: '8px 18px 7px', background: 'var(--paper)', borderBottom: '1px solid var(--rule-soft)' }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>{label}</span>
                  </div>
                  {arr.map((n) => (
                    <NotifRow key={n.id} n={n} onOpen={handleOpenSource} onSnooze={(x) => snooze(x.id)} onToggleRead={handleToggleRead} />
                  ))}
                </div>
              ))}

              {showActivity && (
                <div>
                  <div style={{ position: 'sticky', top: 0, zIndex: 1, padding: '8px 18px 7px', background: 'var(--paper)', borderBottom: '1px solid var(--rule-soft)', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>Activity</span>
                    <button onClick={onClearActivity} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: HEAD, fontWeight: 600, fontSize: 11, color: 'var(--indigo)' }}>Clear</button>
                  </div>
                  {activityLog.map((a) => (
                    <div key={a.id} style={{ padding: '11px 18px', borderBottom: '1px solid var(--rule-soft)' }}>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.4 }}>{a.message}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-ghost)', marginTop: 2 }}>{formatTimeAgo(a.time)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* footer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: '1px solid var(--rule)', background: 'var(--paper-2)' }}>
              <button onClick={openPrefs} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, color: 'var(--indigo)' }}>
                <Icon d={PATHS.gear} size={14} color="var(--indigo)" /> Notification preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </Popover>
  );
}
